import { Button as ReactButton } from 'devextreme-react/button';
import { Popup } from 'devextreme-react/popup';
import notify from 'devextreme/ui/notify';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import grantApi from '../../api/GrantApi';
import { Modules } from '../../dtos/Modules';
import useAsync from '../../hooks/useAsync';
import '../css/Settings.css';
import '../css/GrantDetails.css';
import { useAppContext } from '../shared/AppContext';
import { DATES, MESSAGES, handleError, shortenDate } from '../shared/Utils';
import GrantDetailHeader from './GrantDetailHeader';
import GrantDetailPhases from './GrantDetailPhases';

export default React.memo(function GrantDetails(props) {

    const app = useAppContext();

    const navigate = useNavigate();

    const [state, setState] = useState<any>({});
    const [grantData, setGrantData] = useState<any>({});
    const [phaseData, setPhaseData] = useState<any>({});

    const params = useParams();
    const grantId = params.id;

    const onError = async (error: any) => {
        const message = handleError(error);
        navigate('/error', { state: { props: message } });
    }

    const repopulateData = async () => {
        populateData();
    }

    const getGrantAndPhaseData = async (): Promise<{ grantData: any; phaseData: any }> => {

        if (grantId) {
            app.showLoadPanel();

            const newGrantData = await grantApi.getGrantData(grantId, onError);

            if (newGrantData.status === 401) {
                navigate("/portfolio");
                app.errorToast("You are not authorized to view that report.");
            }

            const dcEnabled = newGrantData?.modules?.some((m: { moduleId: Modules, enabled: boolean }) => m.moduleId == Modules.DC && m.enabled);

            if (dcEnabled) {

                const newPhaseData = await grantApi.getPhaseData(grantId, onError);

                app.hideLoadPanel();

                return { grantData: newGrantData, phaseData: newPhaseData };
            }

            app.hideLoadPanel();

            return { grantData: newGrantData, phaseData: undefined };
        }

        app.hideLoadPanel();

        return { grantData: undefined, phaseData: undefined };
    }

    const populateData = async () => {
        if (grantId) {

            const grantAndPhaseData = await getGrantAndPhaseData();

            setGrantData(grantAndPhaseData.grantData);
            setPhaseData(grantAndPhaseData.phaseData);
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            if (app.loginUser && !grantData.grantId) {
                await populateData();
            }
        };

        fetchData();
    }, [app])

    useEffect(() => {
        setState({
            ...state,
            grant: {},
            grantModified: false,
            showModifiedPopup: false,
            showOverridePopup: false,
            originalDataFields: {},
        });
    }, [])

    const { loading, error, value } = useAsync(async () => {
        if (app.loginUser) {
            await populateData();
        }
    });

    const onBackClick = (e: any) => {
        if (state.grantModified) {
            setState({
                ...state,
                showModifiedPopup: true
            });
        }
        else {
            history.back();
        }
    }

    const onOkClick = (e: any) => {
        setState({
            ...state,
            grantModified: false,
            showModifiedPopup: false,
            showOverridePopup: false,
        });

        history.back();
    };

    const onCancelPopupClick = (e: any) => {
        setState({
            ...state,
            showModifiedPopup: false,
            showOverridePopup: false,
        });
    };

    const updateGrant = async (updatedGrantData: any, updatedPhaseData?: any) => {

        await grantApi.updateGrant(updatedGrantData, updatedPhaseData ?? phaseData);

        notify(MESSAGES.UPDATESSAVED)
    }

    const setShortInstitution = async (shortInstitution: string) => {

        if (grantData.shortInstitution != shortInstitution) {

            const updatedGrantData: any = {
                ...grantData,
                shortInstitution: shortInstitution
            };

            setGrantData(updatedGrantData);

            await updateGrant(updatedGrantData);
        }
    }

    const setShortTitle = async (shortTitle: string) => {

        if (grantData.shortTitle != shortTitle) {

            const updatedGrantData = {
                ...grantData,
                shortTitle: shortTitle
            };

            setGrantData(updatedGrantData);

            await updateGrant(updatedGrantData);
        }
    }

    const setBudgetEnd = async (budgetEndDate: any) => {

        if (moment(shortenDate(state.grant.budgetEndDate)).format(DATES.FORMAT) !== budgetEndDate) {

            const updatedGrantData = {
                ...grantData,
                budgetEndDate: budgetEndDate
            };

            setGrantData(updatedGrantData);

            await updateGrant(updatedGrantData);
        }
    }

    if (!app.loginUser) {
        return (<div></div>);
    } else {
        return (
            <>
                <Popup
                    visible={state.showOverridePopup}
                    dragEnabled={false}
                    position="center"
                    hideOnOutsideClick={false}
                    showCloseButton={false}
                    showTitle={true}
                    title={MESSAGES.EXITPAGECONFIRMATION}
                    width={705}
                    height={120}>
                    <br />
                    <div className="container-fluid formReqPanelTop">
                        <div className="row popupRow">
                            <div className="dx-field col-3">
                            </div>
                            <div className="dx-field col-3">
                                <ReactButton
                                    width={100}
                                    text="Continue"
                                    onClick={onOkClick} />
                            </div>
                            <div className="dx-field col-3">
                                <ReactButton
                                    width={100}
                                    text="Cancel"
                                    onClick={onCancelPopupClick} />
                            </div>
                            <div className="dx-field col-3">
                            </div>
                        </div>
                    </div>
                </Popup>
                <Popup
                    visible={state.showModifiedPopup}
                    dragEnabled={false}
                    position="center"
                    hideOnOutsideClick={false}
                    showCloseButton={false}
                    showTitle={true}
                    title="Unsaved Changes"
                    width={525}
                    height={180}>
                    <br />
                    <div style={{ marginLeft: '30px' }}><b>{MESSAGES.EXITPAGECONFIRMATION}</b></div>
                    <br />
                    <div className="container-fluid formReqPanelTop">
                        <div className="row popupRow">
                            <div className="dx-field col-3">
                            </div>
                            <div className="dx-field col-3">
                                <ReactButton
                                    width={100}
                                    text="Continue"
                                    onClick={onOkClick} />
                            </div>
                            <div className="dx-field col-3">
                                <ReactButton
                                    width={100}
                                    text="Cancel"
                                    onClick={onCancelPopupClick} />
                            </div>
                            <div className="dx-field col-3">
                            </div>
                        </div>
                    </div>
                </Popup>
                <div>
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-lg-12" style={{ marginTop: '10px', paddingLeft: '0px', paddingRight: '0px' }}>
                                <div className="grantDetailsCanvas">
                                    <div className="grantDetailsPanelTop">
                                        <GrantDetailHeader grantData={grantData} phaseData={phaseData} setShortInstitution={setShortInstitution} setShortTitle={setShortTitle} setBudgetEnd={setBudgetEnd} />
                                        <GrantDetailPhases grantData={grantData} repopulateData={repopulateData} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
});